import React from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';
import {FORM_ERROR} from 'final-form';

import EmailAlreadyInUseError from 'coreClasses/EmailAlreadyInUseError';
import EmailValidationError from 'coreClasses/EmailValidationError';
import {EEventSource} from 'coreClasses/events/IEvents';
import {EEventType} from 'coreClasses/events/IEvents';
import {useCustomerMgr, useUI} from 'coreHooks';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import {
	AuthLoginFormError as AuthRegisterFormError,
} from 'brandComponents/auth/AuthLogin/Subcomponents/AuthLoginForm/Subcomponents';
import Button from 'brandComponents/Button';
import ShowOnDesktop from 'brandComponents/Responsive/ShowOnDesktop';
import ShowOnMobile from 'brandComponents/Responsive/ShowOnMobile';
import Typography from 'brandComponents/Typography';
import {NEWSLETTER_SOURCE} from 'brandConstants';

import schema from './helpers';
import {IAuthRegisterFormProps} from './IAuthRegisterForm';
import messages from './messages';
import {
	AuthRegisterFormConsent,
	AuthRegisterFormFields,
} from './Subcomponents';

import styles from './AuthRegisterForm.module.scss';

const AuthRegisterForm = ({
	children,
	onSuccess,
	onCancel,
	isAccountPage = false,
}: IAuthRegisterFormProps) => {
	const CustomerMgr = useCustomerMgr();
	const intl = useIntl();
	const formatEntry = (entry) => [entry[0], intl.formatMessage(entry[1])];
	const {managerRepository} = useGeneralProvider();
	const isRequiresLoginEnabled = useUI('requiresLoginFeature')?.enabled;
	const source = isRequiresLoginEnabled ? NEWSLETTER_SOURCE.SIGN_UP_REQUIRES : NEWSLETTER_SOURCE.SIGN_UP;

	const onValidSubmit = async (values) => {
		const errors = schema.validate(values);

		if (Object.keys(errors).length > 0) {
			return Object.fromEntries(Object.entries(errors).map(formatEntry));
		}

		if (values.phone?.trim() === '') {
			delete values.phone;
			delete values.phoneCode;
		}

		if (!values.birthday?.day && !values.birthday?.month && !values.birthday?.year) {
			delete values.birthday;
		}

		try {
			await CustomerMgr.registerCustomer(values, source);

			managerRepository.getEventsMgr().emit(EEventType.SIGNUP, {
				pageType: EEventSource.SIGN_UP,
				customer: CustomerMgr.getCurrentCustomer()
			});

			onSuccess?.();
		} catch (error) {
			let formError = {};

			switch (true) {
				case error instanceof EmailValidationError:
					formError = {
						[FORM_ERROR]: {
							error: error,
							message: messages.emailIsInvalid,
						},
					};

					break;
				case error instanceof EmailAlreadyInUseError:
					formError = {
						[FORM_ERROR]: {
							error: error,
							message: messages.emailAlreadyInUse,
						},
					};

					break;
				default:
					formError = {
						[FORM_ERROR]: {
							error: error,
							message: messages.unexpectedError,
						},
					};
			}

			return formError;
		}
	};

	return (
		<Form
			onSubmit={onValidSubmit}
			initialValues={{
				privacyAuthorization: false,
				acceptsMarketing: false,
				authorizationDataCollection: false
			}}
			render={({handleSubmit, submitting, hasValidationErrors}) => {
				return (
					<form
						onSubmit={handleSubmit}
						className={cn(styles['auth__register-form'], {
							[styles['submitting']]: submitting,
						})}
					>
						<div className={styles['auth__register-form__header']}>
							<Typography variant="h2">
								{intl.formatMessage(messages.title)}
							</Typography>
						</div>

						{!isAccountPage && (
							<ShowOnDesktop classNames={styles['auth__register-form__header__go-back']}>
								<Button onClick={onCancel} type="button" variant="secondary">
									{intl.formatMessage(messages.recall)}
								</Button>
							</ShowOnDesktop>
						)}

						<ShowOnDesktop>
							<AuthRegisterFormError classNames={styles['auth__register-form__error']} />
						</ShowOnDesktop>

						<AuthRegisterFormFields
							schema={schema}
							classNames={styles['auth__register-form__fields']}
						/>
						<AuthRegisterFormConsent submitting={submitting} />

						{children}

						<ShowOnMobile>
							<AuthRegisterFormError classNames={styles['auth__register-form__error']} />
						</ShowOnMobile>

						<div className={styles['auth__register-form__buttons']}>
							<Button disabled={submitting || hasValidationErrors} type="submit">
								{intl.formatMessage(messages.submit)}
							</Button>
						</div>
					</form>
				);
			}}
		/>
	);
};

export default AuthRegisterForm;
